import { Button } from "@bcc/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { hasManifestEnabledCarrier } from "@bcc/contexts/batches-context.utils";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { CarrierCustomerAccountBinding, LabelPrintBatchCreateRequest, LabelPrintBatchDetail } from "@shared/types";
import { FileSpreadsheet, FileText, Loader2, Package, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getRetryDisabledReason, isBatchDocumentsDisabled, setBatchSummaryActionTestId } from "./batch-summary.utils";

interface BatchSummaryActionsProps {
  readonly batch?: LabelPrintBatchDetail | null;
  readonly customerAccountCarrierDetails?: CarrierCustomerAccountBinding[];
  readonly isProcessing?: boolean;
  readonly isRetrying?: boolean;
  readonly isGeneratingManifest?: boolean;
  readonly retryRequest?: LabelPrintBatchCreateRequest;
  readonly onGenerateManifest?: (batch: LabelPrintBatchDetail) => void;
  readonly onReprintLabels?: (batch: LabelPrintBatchDetail) => void;
  readonly onReprintPackingSlips?: (batch: LabelPrintBatchDetail) => void;
  readonly onReprintLabelsAndPackingSlips?: (batch: LabelPrintBatchDetail) => void;
  readonly onOpenSummary?: (batch: LabelPrintBatchDetail) => void;
  readonly onRetryBatch?: (request: LabelPrintBatchCreateRequest) => void | Promise<void>;
}

export function BatchSummaryActions({
  batch,
  customerAccountCarrierDetails,
  isProcessing,
  isRetrying,
  isGeneratingManifest = false,
  retryRequest,
  onGenerateManifest,
  onReprintLabels,
  onReprintPackingSlips,
  onReprintLabelsAndPackingSlips,
  onOpenSummary,
  onRetryBatch,
}: BatchSummaryActionsProps) {
  const { t } = useTranslation();
  const [{ currentVendor }] = useVendorContext();
  const hasBatch = Boolean(batch);
  const areDocumentsDisabled = isBatchDocumentsDisabled(batch) || Boolean(isProcessing);
  const retryDisabledReason = getRetryDisabledReason({ isProcessing, isRetrying, batchStatus: batch?.batchStatus });
  const isRetryDisabled = Boolean(retryDisabledReason);
  const retryDisabledTitle = retryDisabledReason === "processing" && t("buttons.retryDisabledWhileProcessing");

  const showShippingLabels = currentVendor?.properties?.showShippingLabelsButton !== false;
  const showPackingSlips = currentVendor?.properties?.showPackingSlipsButton !== false;
  const showManifest = currentVendor?.properties?.showManifestButton !== false;

  // Button layout is driven by the vendor's `batchPrintPreference` radio
  // (Preferences → Printing → Batch Printing). Defaults to LabelsOnly so
  // vendors without the property set see the full individual button row.
  // Combined mode is only active when both label types are visible.
  const isCombined = showShippingLabels && showPackingSlips && currentVendor?.properties?.batchPrintPreference === "LabelsWithPackingSlips";

  const canShowManifest = hasManifestEnabledCarrier(batch, customerAccountCarrierDetails);

  if (!hasBatch) {
    return null;
  }

  const retryButton = retryRequest && retryDisabledReason !== "retrying" && (
    <Button
      variant="outline"
      onClick={() => onRetryBatch?.(retryRequest)}
      disabled={isRetryDisabled}
      data-testid={setBatchSummaryActionTestId("retry-batch")}
    >
      {t("buttons.retry")}
    </Button>
  );

  return (
    <div className="flex flex-wrap shrink-0 items-center justify-end gap-2">
      {retryButton &&
        (retryDisabledTitle ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                data-testid={setBatchSummaryActionTestId("retry-batch-tooltip-trigger")}
              >
                {retryButton}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{retryDisabledTitle}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          retryButton
        ))}
      {isCombined && (
        <Button
          variant="outline"
          onClick={() => batch && (onReprintLabelsAndPackingSlips ?? onReprintLabels)?.(batch)}
          disabled={areDocumentsDisabled}
          data-testid={setBatchSummaryActionTestId("shipping-labels-and-packing-slips")}
        >
          <Printer className="mr-2 h-4 w-4" />
          {t("views.batchSummary.shippingLabelsAndPackingSlips")}
        </Button>
      )}
      {!isCombined && showShippingLabels && (
        <Button
          variant="outline"
          onClick={() => batch && onReprintLabels?.(batch)}
          disabled={areDocumentsDisabled}
          data-testid={setBatchSummaryActionTestId("shipping-labels")}
        >
          <Printer className="mr-2 h-4 w-4" />
          {t("views.batchSummary.shippingLabels")}
        </Button>
      )}
      {!isCombined && showPackingSlips && (
        <Button
          variant="outline"
          onClick={() => batch && onReprintPackingSlips?.(batch)}
          disabled={areDocumentsDisabled}
          data-testid={setBatchSummaryActionTestId("packing-slips")}
        >
          <Package className="mr-2 h-4 w-4" />
          {t("views.batchSummary.packingSlips")}
        </Button>
      )}
      {showManifest && canShowManifest && (
        <Button
          variant="outline"
          onClick={() => batch && onGenerateManifest?.(batch)}
          disabled={areDocumentsDisabled || isGeneratingManifest}
          aria-busy={isGeneratingManifest}
          data-testid={setBatchSummaryActionTestId("manifest")}
        >
          {isGeneratingManifest ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" data-testid={setBatchSummaryActionTestId("manifest-loading")} />
          ) : (
            <FileText className="mr-2 h-4 w-4" />
          )}
          {t("views.batchSummary.manifest")}
        </Button>
      )}
      <Button
        variant="outline"
        onClick={() => batch && onOpenSummary?.(batch)}
        disabled={areDocumentsDisabled}
        data-testid={setBatchSummaryActionTestId("summary")}
      >
        <FileSpreadsheet className="mr-2 h-4 w-4" />
        {t("views.batchSummary.summary")}
      </Button>
    </div>
  );
}
